/**
 * Phase 10 R5 — useDailyFinancialSnapshot
 *
 * Aggregates ticket-level GP for a given day into a single snapshot card.
 *
 * HARD CONTRACT (per R5 review tightening #4):
 *   This hook MUST NOT call `useTicketFinancialSummary()`,
 *   `useTicketPartsLines`, `useTicketLaborLines`, or `useTicketTechPayLines`
 *   inside a `.map()` over tickets — that would violate React rules-of-hooks
 *   AND trigger N+1 fetches.
 *
 * Architecture:
 *   1. **One** Supabase query for the day's tickets (`tickets` filter).
 *   2. **Three** Supabase queries via batched day-hooks (parts, labor, pay).
 *   3. In-memory grouping by ticket_id.
 *   4. Per-ticket call to the **pure helper** `computeTicketFinancialSummary`
 *      (NOT the React hook), then `computeFinancialWarnings`.
 *   5. Aggregate per-bucket per the R5 inclusion-policy.
 *
 * Total network cost: **4 queries**, regardless of ticket count.
 *
 * INCLUSION POLICY (per R5 review tightening #2):
 *   • Parts totals include tickets where `partsStatus !== 'insufficient'`
 *   • Labor sell totals include tickets where `laborSellStatus !== 'insufficient'`
 *   • Labor cost totals include tickets where `laborCostStatus !== 'insufficient'`
 *   • Combined GP is derived from the *included* sell/cost — never invented
 *   • "Below target" / "negative GP" counts evaluated only on **included** tickets
 *
 * VISIBILITY:
 *   Manager-tier always; SERVICE_WRITER per shop visibility mode;
 *   TECHNICIAN never. Returns `enabled: false` when not permitted.
 *
 * AUTHORITY (FF-A / FF-B / FF-E):
 *   • Labor + tech-pay column WRITES gated by FF-A
 *     (`trg_enforce_labor_entitlement`).
 *   • parts_lines WRITES gated by FF-B
 *     (`trg_enforce_parts_line_entitlement`).
 *   • parts_lines READS additionally gated by FF-E
 *     (`ff_e_parts_lines_select_entitled` RESTRICTIVE policy).
 *   • ticket_jobs labor / tech_pay column READS deferred — see
 *     `useTicketFinancialSummary` doc block for rationale.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useUserRole } from '@/hooks/useUserRole';
import { useShopFinancialSettings } from '@/hooks/labor/useShopFinancialSettings';
import { useDayPartsLines } from '@/hooks/labor/useDayPartsLines';
import { useDayLaborLines } from '@/hooks/labor/useDayLaborLines';
import { useDayTechPayLines } from '@/hooks/labor/useDayTechPayLines';
import {
  computeTicketFinancialSummary,
  type TicketFinancialSummary,
} from '@/lib/labor/computeTicketFinancialSummary';
import {
  computeFinancialWarnings,
  isActionable,
  worstSeverity,
  type FinancialWarning,
  type WarningSeverity,
} from '@/lib/labor/computeFinancialWarnings';
import { canSeeFinancials } from '@/lib/labor/resolveFinancialVisibility';
import type { TicketLaborTotals } from '@/hooks/labor/useTicketLaborLines';
import type {
  TicketTechPayLine,
  TicketTechPayTotals,
} from '@/hooks/labor/useTicketTechPayLines';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

export interface DailyFinancialSnapshot {
  /** Aggregated parts sell/cost/GP — only includes tickets with parts data. */
  parts: { sell: number; cost: number; gpAmount: number; gpPercent: number };
  /** Aggregated labor sell — bucketed independently from labor cost. */
  laborSell: number;
  /** Aggregated labor cost — only includes tickets where pay is computable. */
  laborCost: number;
  /** Combined sell/cost/GP — derived from the included buckets. */
  combined: { sell: number; cost: number; gpAmount: number; gpPercent: number };
  /** Counts surfaced so users see what was included/excluded. */
  ticket_count: number;
  complete_ticket_count: number;
  partial_ticket_count: number;
  insufficient_ticket_count: number;
  tickets_excluded_from_parts_cost: number;
  tickets_excluded_from_labor_cost: number;
  /** Warning counts evaluated only on **included** tickets. */
  low_margin_ticket_count: number;
  negative_gp_ticket_count: number;
  /** Top 3 lowest-GP tickets (by combined gpAmount asc). */
  lowest_gp_tickets: Array<{
    ticket_id: string;
    ticket_number: string | null;
    gpAmount: number;
    gpPercent: number;
    severity: WarningSeverity | null;
  }>;
}

export interface UseDailyFinancialSnapshotResult {
  snapshot: DailyFinancialSnapshot | null;
  isLoading: boolean;
  enabled: boolean;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

interface DayTicketRow {
  id: string;
  ticket_number: string | null;
}

export function useDailyFinancialSnapshot(
  dateStr: string | undefined,
): UseDailyFinancialSnapshotResult {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayFlag = useExposureFlag('ui_technician_pay');
  const { role } = useUserRole();
  const { settings } = useShopFinancialSettings();

  const allowed = canSeeFinancials(role, settings.financial_visibility_mode);
  const enabled =
    !!shopId &&
    !!dateStr &&
    laborEngineEnabled &&
    ticketFinancialsEnabled &&
    allowed;

  // Q1 — day's tickets (one query, list of ids).
  const { data: dayTickets, isLoading: ticketsLoading } = useQuery<
    DayTicketRow[]
  >({
    queryKey: ['day-tickets-for-financials', shopId, dateStr],
    enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tickets')
        .select('id, ticket_number')
        .eq('shop_id', shopId)
        .eq('scheduled_date', dateStr)
        // DMC-D3a: canonical active-ticket filter — both reps
        // excluded. The day's ticket list feeds the financial
        // snapshot's per-ticket fan-out; a single ghost-deleted row
        // would have inflated `ticket_count` and the lowest-GP top-3.
        .neq('status', 'Deleted')
        .is('deleted_at', null);
      if (error) throw error;
      return (data ?? []) as DayTicketRow[];
    },
  });

  // Q2/3/4 — batched parts/labor/pay across the whole day.
  const { data: partsLines, isLoading: partsLoading } = useDayPartsLines(
    dateStr,
    enabled,
  );
  const { data: laborLines, isLoading: laborLoading } = useDayLaborLines(
    dateStr,
    enabled,
  );
  const techPayQueryEnabled = enabled && techPayFlag;
  const { data: techPayLines, isLoading: payLoading } = useDayTechPayLines(
    dateStr,
    techPayQueryEnabled,
  );

  const isLoading =
    enabled &&
    (ticketsLoading ||
      partsLoading ||
      laborLoading ||
      (techPayQueryEnabled && payLoading));

  const snapshot = useMemo<DailyFinancialSnapshot | null>(() => {
    if (!enabled) return null;
    if (isLoading) return null;
    if (!dayTickets) return null;

    // Group by ticket_id in-memory.
    const partsByTicket = groupBy(partsLines ?? [], (l) => l.ticket_id);
    const laborByTicket = groupBy(laborLines ?? [], (l) => l.ticket_id);
    const payByTicket = groupBy(techPayLines ?? [], (l) => l.ticket_id);

    const agg = {
      partsSell: 0,
      partsCost: 0,
      laborSell: 0,
      laborCost: 0,
      ticket_count: dayTickets.length,
      complete_ticket_count: 0,
      partial_ticket_count: 0,
      insufficient_ticket_count: 0,
      tickets_excluded_from_parts_cost: 0,
      tickets_excluded_from_labor_cost: 0,
      low_margin_ticket_count: 0,
      negative_gp_ticket_count: 0,
    };

    type LowEntry = DailyFinancialSnapshot['lowest_gp_tickets'][number] & {
      includedInCombined: boolean;
    };
    const evaluated: LowEntry[] = [];

    for (const t of dayTickets) {
      const pLines = partsByTicket.get(t.id) ?? [];
      const lLines = laborByTicket.get(t.id) ?? [];
      const pyLines = payByTicket.get(t.id) ?? [];

      const laborTotals: TicketLaborTotals = computeLaborTotals(lLines);
      const payTotals: TicketTechPayTotals | null = techPayQueryEnabled
        ? computePayTotals(pyLines)
        : null;

      const summary = computeTicketFinancialSummary({
        partsLines: pLines,
        laborTotals,
        techPayLines: techPayQueryEnabled ? pyLines : null,
        techPayTotals: payTotals,
        matrix: null,
      });

      // Bucket inclusion per R5 policy.
      const includeParts = summary.completeness.partsStatus !== 'insufficient';
      const includeLaborSell =
        summary.completeness.laborSellStatus !== 'insufficient';
      const includeLaborCost =
        summary.completeness.laborCostStatus !== 'insufficient';

      if (includeParts) {
        agg.partsSell += summary.parts.sell;
        agg.partsCost += summary.parts.cost;
      } else {
        agg.tickets_excluded_from_parts_cost += 1;
      }
      if (includeLaborSell) agg.laborSell += summary.labor.sell;
      if (includeLaborCost) {
        agg.laborCost += summary.labor.cost;
      } else {
        agg.tickets_excluded_from_labor_cost += 1;
      }

      // Per-ticket completeness rollup.
      switch (summary.completeness.overallStatus) {
        case 'complete':
          agg.complete_ticket_count += 1;
          break;
        case 'partial':
          agg.partial_ticket_count += 1;
          break;
        case 'insufficient':
          agg.insufficient_ticket_count += 1;
          break;
      }

      // Warnings — evaluated only when ticket is meaningfully included
      // (i.e. has at least one non-insufficient bucket).
      const includedInCombined =
        includeParts || includeLaborSell || includeLaborCost;
      let warnings: FinancialWarning[] = [];
      let severity: WarningSeverity | null = null;
      if (includedInCombined) {
        warnings = computeFinancialWarnings(summary, {
          parts: settings.target_parts_gp_percent,
          labor: settings.target_labor_gp_percent,
          total: settings.target_total_gp_percent,
          lowMargin: settings.low_margin_warn_threshold,
        });
        severity = worstSeverity(warnings);
        if (warnings.some((w) => w.code === 'negative_gp'))
          agg.negative_gp_ticket_count += 1;
        // "low margin" ticket = either low_margin floor OR below_target fired
        if (
          warnings.some(
            (w) =>
              w.code === 'total_low_margin' ||
              w.code === 'total_below_target' ||
              w.code === 'parts_below_target' ||
              w.code === 'labor_below_target',
          ) &&
          isActionable(severity)
        ) {
          agg.low_margin_ticket_count += 1;
        }
      }

      evaluated.push({
        ticket_id: t.id,
        ticket_number: t.ticket_number ?? null,
        gpAmount: summary.combined.gpAmount,
        gpPercent: summary.combined.gpPercent,
        severity,
        includedInCombined,
      });
    }

    // Combined derived from the included buckets only.
    const sellCombined = r2(agg.partsSell + agg.laborSell);
    const costCombined = r2(agg.partsCost + agg.laborCost);
    const gpCombined = r2(sellCombined - costCombined);
    const pctCombined =
      sellCombined > 0 ? r2(((sellCombined - costCombined) / sellCombined) * 100) : 0;

    const lowest = evaluated
      .filter((e) => e.includedInCombined)
      .sort((a, b) => a.gpAmount - b.gpAmount)
      .slice(0, 3)
      .map(({ includedInCombined: _i, ...rest }) => rest);

    return {
      parts: {
        sell: r2(agg.partsSell),
        cost: r2(agg.partsCost),
        gpAmount: r2(agg.partsSell - agg.partsCost),
        gpPercent:
          agg.partsSell > 0
            ? r2(((agg.partsSell - agg.partsCost) / agg.partsSell) * 100)
            : 0,
      },
      laborSell: r2(agg.laborSell),
      laborCost: r2(agg.laborCost),
      combined: {
        sell: sellCombined,
        cost: costCombined,
        gpAmount: gpCombined,
        gpPercent: pctCombined,
      },
      ticket_count: agg.ticket_count,
      complete_ticket_count: agg.complete_ticket_count,
      partial_ticket_count: agg.partial_ticket_count,
      insufficient_ticket_count: agg.insufficient_ticket_count,
      tickets_excluded_from_parts_cost: agg.tickets_excluded_from_parts_cost,
      tickets_excluded_from_labor_cost: agg.tickets_excluded_from_labor_cost,
      low_margin_ticket_count: agg.low_margin_ticket_count,
      negative_gp_ticket_count: agg.negative_gp_ticket_count,
      lowest_gp_tickets: lowest,
    };
  }, [
    enabled,
    isLoading,
    dayTickets,
    partsLines,
    laborLines,
    techPayLines,
    techPayQueryEnabled,
    settings.target_parts_gp_percent,
    settings.target_labor_gp_percent,
    settings.target_total_gp_percent,
    settings.low_margin_warn_threshold,
  ]);

  return { snapshot, isLoading: !!isLoading, enabled };
}

// ── Helpers ────────────────────────────────────────────────────────────────

function groupBy<T, K>(arr: T[], keyOf: (t: T) => K): Map<K, T[]> {
  const m = new Map<K, T[]>();
  for (const item of arr) {
    const k = keyOf(item);
    const bucket = m.get(k);
    if (bucket) bucket.push(item);
    else m.set(k, [item]);
  }
  return m;
}

/**
 * Re-derives R2 totals from a slice of TicketLaborLine rows. Mirrors
 * `useTicketLaborLines`'s reducer exactly.
 */
function computeLaborTotals(
  lines: { labor_sell_amount: number | null; labor_rate_overridden: boolean }[],
): TicketLaborTotals {
  return lines.reduce<TicketLaborTotals>(
    (acc, l) => {
      if (l.labor_sell_amount != null) {
        acc.total_labor_sell += l.labor_sell_amount;
        acc.computed_count += 1;
      } else {
        acc.uncomputed_count += 1;
      }
      if (l.labor_rate_overridden) acc.overridden_count += 1;
      return acc;
    },
    {
      total_labor_sell: 0,
      computed_count: 0,
      uncomputed_count: 0,
      overridden_count: 0,
    },
  );
}

/**
 * Re-derives R3 tech-pay totals from a slice of TicketTechPayLine rows.
 * Mirrors `useTicketTechPayLines`'s reducer exactly. `computed`-only by
 * contract — never fabricates a labor cost number.
 */
function computePayTotals(lines: TicketTechPayLine[]): TicketTechPayTotals {
  const t = lines.reduce<TicketTechPayTotals>(
    (acc, l) => {
      if (l.tech_pay_status === 'computed' && l.tech_pay_amount != null) {
        acc.total_tech_pay_amount += l.tech_pay_amount;
        acc.computed_count += 1;
      } else if (l.tech_pay_status === 'deferred_hourly') {
        acc.deferred_hourly_count += 1;
      } else if (l.tech_pay_status === 'no_config') {
        acc.no_config_count += 1;
      } else {
        acc.uncomputed_count += 1;
      }
      return acc;
    },
    {
      total_tech_pay_amount: 0,
      computed_count: 0,
      deferred_hourly_count: 0,
      no_config_count: 0,
      uncomputed_count: 0,
    },
  );
  t.total_tech_pay_amount = Math.round(t.total_tech_pay_amount * 100) / 100;
  return t;
}

// Avoid unused-import warnings for type-only imports kept for reference.
export type _ReexportedTypes = PartsLine | TicketTechPayLine | TicketLaborTotals;
